import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import {
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  ServerStackIcon,
  FolderIcon,
  ClockIcon,
  CommandLineIcon,
  ShieldCheckIcon,
  CpuChipIcon,
} from "@heroicons/react/24/outline";
import Button from "src/components/Shared/Buttons/Button";
import { formatByteSize } from "src/helpers/customAppHelper";
import moment from "moment";

export const CustomAppOverview = ({ data }) => {
  return (
    <div className="animate-fade-in space-y-6 pt-2">
      {/* 1. Live Public Endpoint Card */}
      <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50/70 via-purple-50/40 to-white p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white text-indigo-600 shadow-sm ring-1 ring-indigo-100">
              <GlobeAltIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900">
                  Production Endpoint
                </h3>
                {data?.publicUrl?.startsWith("https") && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                    <ShieldCheckIcon className="h-3 w-3" />
                    HTTPS
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Globally distributed custom domain routed to container ingress
              </p>
            </div>
          </div>

          <div className="flex-1 lg:max-w-xl">
            {data?.publicUrl ? (
              <div className="relative flex items-center rounded-xl border border-indigo-200/80 bg-white p-1.5 shadow-sm transition-all focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100">
                <input
                  type="text"
                  readOnly
                  value={data.publicUrl}
                  className="h-9 w-full rounded-lg border-0 bg-transparent pl-3 pr-28 font-mono text-xs font-semibold text-indigo-950 focus:ring-0"
                />
                <div className="absolute right-2.5 flex items-center gap-1.5">
                  <CopyToClipboard
                    text={data.publicUrl}
                    onCopy={() => toast.success("Endpoint URL copied")}
                  >
                    <Button
                      type="button"
                      version="default"
                      className="flex !h-8 !w-8 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600 shadow-2xs hover:bg-gray-100 hover:text-gray-900"
                      title="Copy URL"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </Button>
                  </CopyToClipboard>
                  <a
                    href={data.publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex !h-8 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition"
                  >
                    <span>Visit</span>
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white/70 p-3 text-center text-xs text-gray-400">
                Endpoint will be provisioned once application is published
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2-Column Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Runtime & Container Specifications */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs hover:shadow-sm transition-shadow">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
              <ServerStackIcon className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">
                Container Architecture
              </h4>
              <p className="text-xs text-gray-500">
                Isolated Docker container execution runtime
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Container Name
              </span>
              <span
                className="mt-1 block font-mono text-xs font-semibold text-slate-800 truncate"
                title={data?.containerName}
              >
                {data?.containerName || "—"}
              </span>
            </div>

            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Internal Hostname
              </span>
              <span
                className="mt-1 block font-mono text-xs font-semibold text-slate-800 truncate"
                title={data?.hostname}
              >
                {data?.hostname || "—"}
              </span>
            </div>

            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Network Bridge
              </span>
              <span
                className="mt-1 block font-mono text-xs font-semibold text-slate-800 truncate"
                title={data?.networkName}
              >
                {data?.networkName || "—"}
              </span>
            </div>

            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Container ID
              </span>
              <span
                className="mt-1 block font-mono text-xs font-semibold text-slate-800 truncate"
                title={data?.containerId}
              >
                {data?.containerId ? data.containerId.slice(0, 12) : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Package & Build Artifacts */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs hover:shadow-sm transition-shadow">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <FolderIcon className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">
                Package & Source Code
              </h4>
              <p className="text-xs text-gray-500">
                Deploy artifact and unpack statistics
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Archive File
              </span>
              <span
                className="mt-1 block font-mono text-xs font-semibold text-slate-800 truncate"
                title={data?.sourceFileName}
              >
                {data?.sourceFileName || "—"}
              </span>
            </div>

            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                File Count
              </span>
              <span className="mt-1 block text-sm font-bold text-slate-900">
                {data?.fileCount ? `${data.fileCount} files` : "—"}
              </span>
            </div>

            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Bundle Size
              </span>
              <span className="mt-1 block text-sm font-bold text-slate-900">
                {formatByteSize(data?.byteSize)}
              </span>
            </div>
          </div>

          {/* Start Command */}
          {data?.startCommand && (
            <div className="mt-3 rounded-xl border border-gray-800 bg-gray-950 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
                  <CommandLineIcon className="h-3.5 w-3.5 text-gray-400" />
                  <span>Start Command</span>
                </div>
                <CopyToClipboard
                  text={data.startCommand}
                  onCopy={() => toast.success("Command copied")}
                >
                  <button
                    type="button"
                    className="text-[10px] font-mono text-gray-400 hover:text-white transition-colors"
                  >
                    Copy
                  </button>
                </CopyToClipboard>
              </div>
              <code className="block font-mono text-xs text-emerald-400 truncate">
                {data.startCommand}
              </code>
            </div>
          )}
        </div>
      </div>

      {/* 3. Deployment Lifecycle Timestamps */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <ClockIcon className="h-5 w-5" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-900">
              Deployment Lifecycle & Timeline
            </h4>
            <p className="text-xs text-gray-500">
              History of publication, builds, and updates
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
          <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-3.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-gray-500 shadow-2xs border border-gray-100">
              <CpuChipIcon className="h-5 w-5" />
            </div>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Created
              </span>
              <span className="text-xs font-semibold text-gray-800">
                {data?.createdAt
                  ? moment(data.createdAt).format("MMM DD, YYYY · HH:mm")
                  : "—"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-3.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-emerald-600 shadow-2xs border border-emerald-100">
              <GlobeAltIcon className="h-5 w-5" />
            </div>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Last Deployed
              </span>
              <span className="text-xs font-semibold text-gray-800">
                {data?.deployedAt
                  ? moment(data.deployedAt).format("MMM DD, YYYY · HH:mm")
                  : "Not yet deployed"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-3.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-indigo-600 shadow-2xs border border-indigo-100">
              <ClockIcon className="h-5 w-5" />
            </div>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Last Updated
              </span>
              <span className="text-xs font-semibold text-gray-800">
                {data?.updatedAt
                  ? moment(data.updatedAt).format("MMM DD, YYYY · HH:mm")
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
